import { lazy, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import '../styles/dashboard.scss';
import Navbar from '../components/navbar';
import Sidebar from '../components/sidebar';
import TopLoaderComponent from '../components/topLoader/TopLoaderComponent';
import LiveChatWidget from '../components/elements/LiveChatWidget';
import Button from '../components/form-control/Button';
import { getCompanyOnboardingStatusFromState, getTermsAndConditionsDismissed, getTopLoadingState, REMOVE_PENDING_ACTION, SET_TERMS_AND_CONDITIONS_DISMISSED } from '../states/slices/globalSlice';
import useAuth from '../hooks/authHook';
import { LAUNCH_CHAT } from '../states/slices/liveChatSlice';
import useModal from '../hooks/modalHook';
import useUserAccount from '../hooks/userAccountHook';
import { getTrialEndDaysFromDate } from '../utils/miscFunctions';


type DashboardLayoutProps = {
  showSideBar: boolean;
};

const termsActionText = 'Terms and conditions have not been accepted. Please review our terms and conditions and accept them';

// Lazy load the floating calculator button
const LazyTermsAndConditions = lazy(() =>
  import('../views/shared/settings/terms-and-conditions')
    .then(module => ({ default: module.default }))
);

const DashboardLayout = ({ showSideBar }: DashboardLayoutProps): JSX.Element => {
  const { user } = useUserAccount();
  const [showSidebar, setShowSidebar] = useState(false);
  const showTopLoading = useSelector(getTopLoadingState);
  const { isInsurerOnboarding } = useAuth();
  const dispatch = useDispatch();
  const onboardingStatus = useSelector(getCompanyOnboardingStatusFromState);
  const termsDismissed = useSelector(getTermsAndConditionsDismissed) || true;
  const { ModalComponent, updateModalStates, closeModal } = useModal();

  const showDashboardLayout = !isInsurerOnboarding() && !window.location.pathname.includes('/store/preview');

  useEffect(() => {
    if (!showDashboardLayout) {
      setTimeout(() => {
        document
          .querySelectorAll('.bot--bubble-holder')
          .forEach((a: any) => (a.style.display = 'none'));
      }, 1500);
    } else {
      document
        .querySelectorAll('.bot--bubble-holder')
        .forEach((a: any) => (a.style.display = 'initial'));
    }
  }, [showDashboardLayout]);

  const handleMenuClick = () => {
    setShowSidebar((prev) => !prev);
  };

  const handleSidebarMenuClicked = () => {
    setShowSidebar((prev) => !prev);
  };

  const trialDaysLeft = getTrialEndDaysFromDate(onboardingStatus?.companyInfo?.trialExpiry || onboardingStatus?.companyInfo?.signedOn);

  const showTermsAndConditions = () => {
    if (onboardingStatus?.companyInfo?.id && !onboardingStatus?.companyInfo?.termsAndConditionsAccepted && user?.isBrokerOwner() && !termsDismissed) {
      updateModalStates({
        show: true,
        size: '7xl',
        onClose() {
          dispatch(SET_TERMS_AND_CONDITIONS_DISMISSED(true))
          dispatch(REMOVE_PENDING_ACTION(termsActionText));
          closeModal()
        },
        children: (
          <LazyTermsAndConditions
            hideControls
            onSuccess={() => {
              dispatch(REMOVE_PENDING_ACTION(termsActionText));
              closeModal()
            }}
          />
        ),
        showCloseButton: typeof trialDaysLeft?.days === 'number' ? trialDaysLeft.days > 0 : false,
        
        classes: '!p-0 r-[5px]',
        static: true
      })
    }
  }

  useEffect(() => {
    if (onboardingStatus?.companyInfo?.id !== undefined && !onboardingStatus?.companyInfo?.termsAndConditionsAccepted && !termsDismissed) {
      showTermsAndConditions()
    }
  }, [onboardingStatus?.companyInfo?.id])


  return (
    <>
      {showTopLoading && (
        <TopLoaderComponent
          isLoading={showTopLoading}
          pastDelay={false}
          timedOut={false}
          retry={() => { }}
          error={null}
        />
      )}

      <main>
        {!showDashboardLayout ? (
          <>
            {!window.location.pathname.includes('/store/preview') && (
              <Navbar handleMenuClick={handleMenuClick} hideProfileSection isSticky={false} />
            )}
            <Outlet />
            <Button
              className="fixed right-0 bottom-0 rounded-[27px] bg-primary-main px-6 mr-2 mb-2 border-primary-main"
              onClick={() => {
                dispatch(LAUNCH_CHAT(true));
              }}>
              <QuestionMarkCircleIcon className="w-4 mr-1 text-white dark:text-gray-900" /> Help
            </Button>
          </>
        ) : (
          <>
            <div className="flex w-full md:h-screen bg-[#F4F4F4] dark:bg-gray-900">
              {showSideBar ? (
                <>
                <div className='absolute z-[999] sm:relative'>
                  <Sidebar
                    onMenuItemClicked={handleSidebarMenuClicked}
                    showSidebar={showSidebar}
                  />
                </div>
                  
                  <div className="w-full overflow-x-hidden overflow-y-auto bg-[#F4F4F4] dark:bg-gray-900 hideScrollBar min-h-full">
                    <Navbar handleMenuClick={handleMenuClick} />
                    <div className='p-5 space-y-5 max-h-[calc(100vh_-_80px)] overflow-y-auto'>
                      <Outlet />
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full overflow-x-hidden overflow-y-auto md:h-[calc(100vh_-_80px)] min-h-full space-y-5 bg-[#F4F4F4] dark:bg-gray-900">
                  <Outlet />
                </div>
              )}
            </div>
          </>
        )}
        <ModalComponent />
        <LiveChatWidget />
      </main>
    </>
  );
};

export default DashboardLayout;
